import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import IndexApp from "../../Components/IndexApp";
import ApiVersionRepository, { ApiVersionSummary } from "../../Repositories/ApiVersionRepository";
import { getActiveBrandConfig } from "../../config/environment.config";
import { DV2 } from "../../App/DeclarationV2/theme";
import { Button, Card, TextLink, fieldStyle, useDv2Theme } from "../../App/DeclarationV2/layout/Ui";
import {
    APP_VERSION,
    ChangelogCategory,
    ChangelogEntry,
    VersionInfo,
    apiVersionKey,
    changelogEntryPath,
    countLabel,
    featuredEntry,
    groupChangelog,
} from "../../Utils/changelogView";

/** Icône de chaque rubrique du changelog (fenêtre de version). */
export const CATEGORY_ICONS: Record<ChangelogCategory, React.ReactNode> = {
    declaration: <EventNoteOutlinedIcon fontSize="small" />,
    clients: <GroupOutlinedIcon fontSize="small" />,
    documents: <DescriptionOutlinedIcon fontSize="small" />,
    autres: <AutoAwesomeOutlinedIcon fontSize="small" />,
};

/** Aperçu « calendrier » du bloc principal : deux semaines, les jeudis cochés (illustration de la maquette). */
const CALENDAR_DAYS = Array.from({ length: 14 }, (_, i) => 15 + i);
const CALENDAR_CHECKED = [15, 22];

/**
 * Page « Quoi de neuf » du tableau de bord (profs uniquement), repensée (maquette
 * 13a du 2 novembre 2026) : la nouveauté principale en tête avec un aperçu et un
 * bouton pour l'essayer, puis les nouveautés par groupe (ce que fait le prof), la
 * première de chaque groupe ouverte, chacune disant où la trouver. Un sélecteur
 * permet de relire les versions précédentes. Même requête et même cache que la
 * fenêtre de version (clé ['app-version', version]).
 */
const WhatsNewPage = () => {
    const { primary, fontFamily, text, brandName } = useDv2Theme();
    const navigate = useNavigate();
    const currentUser = useSelector((state: any) => state?.user?.currentUser);
    const account = currentUser?.isClient ? currentUser?.client : currentUser?.teachr;
    const [version, setVersion] = useState(APP_VERSION);
    const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
    const [flashKey, setFlashKey] = useState<string | null>(null);
    const entryRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const { data: versionInfo, isLoading, isError } = useQuery<VersionInfo>({
        queryKey: ["app-version", version],
        queryFn: () => ApiVersionRepository.getVersion(apiVersionKey(version)),
        staleTime: Infinity,
        retry: false,
    });
    const { data: versionsData } = useQuery<{ versions: ApiVersionSummary[] }>({
        queryKey: ["app-versions"],
        queryFn: () => ApiVersionRepository.listVersions(),
        staleTime: Infinity,
        retry: false,
    });
    const versions = useMemo(() => {
        const list = versionsData?.versions ?? [];
        return list.some((v) => v.version === APP_VERSION) ? list : [{ version: APP_VERSION, changelogCount: 0 }, ...list];
    }, [versionsData]);

    const groups = groupChangelog(versionInfo?.changelog);
    const total = groups.reduce((sum, g) => sum + g.entries.length, 0);
    const featured = featuredEntry(versionInfo?.changelog);
    const entryKey = (groupKey: string, index: number) => `${groupKey}-${index}`;

    // La première nouveauté de chaque groupe est ouverte, pour qu'on voie tout de suite qu'il y a un texte dessous.
    useEffect(() => {
        setOpenKeys(new Set(groups.map((g) => entryKey(g.key, 0))));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [versionInfo]);

    const toggle = (key: string) => setOpenKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    /** « Voir comment ça marche » : ouvre la nouveauté principale dans sa rubrique et y descend. */
    const showFeaturedInList = () => {
        if (!featured) return;
        const group = groups.find((g) => g.entries.includes(featured));
        if (!group) return;
        const key = entryKey(group.key, group.entries.indexOf(featured));
        setOpenKeys((prev) => new Set(prev).add(key));
        setFlashKey(key);
        window.setTimeout(() => entryRefs.current[key]?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
        window.setTimeout(() => setFlashKey(null), 2000);
    };

    const tryFeatured = () => {
        const path = featured ? changelogEntryPath(featured) : null;
        if (path) navigate(path);
    };
    const helpPath = `/${getActiveBrandConfig()}/tableau-de-bord/aide-contact`;
    const giveFeedback = () => navigate(helpPath, { state: { subject: "Autre demande", context: `Avis sur la version ${version}` } });

    const whereLink = (entry: ChangelogEntry) => {
        if (!entry.where) return null;
        const path = changelogEntryPath(entry);
        return path
            ? <TextLink label={entry.where} onClick={() => navigate(path)} size={13} />
            : <span style={{ fontSize: 13, color: primary, fontWeight: 800 }}>{entry.where}</span>;
    };

    const subtitle = isLoading
        ? `Version ${version}. Chargement des nouveautés…`
        : total === 0
            ? `Version ${version}. Aucune nouveauté enregistrée pour cette version.`
            : `Version ${version}. ${countLabel(total)}, les plus utiles d'abord.`;

    return (
        <IndexApp
            navBar={{ index: 0 }}
            teachr={account}
            component={
                <main className="px-4" style={{ background: DV2.pageBg, minHeight: "calc(100vh - 64px)", fontFamily, color: text }}>
                    <div className="mx-auto w-full px-4 sm:px-6 py-4 lg:py-8 flex flex-col gap-6">
                        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                            <div>
                                <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, lineHeight: 1.2 }}>Quoi de neuf dans {brandName}</h1>
                                <div style={{ fontSize: 14, color: DV2.muted, marginTop: 4 }}>{subtitle}</div>
                            </div>
                            {versions.length > 1 && (
                                <label className="flex items-center gap-2" style={{ fontSize: 13, color: DV2.muted, fontWeight: 700 }}>
                                    <span className="sr-only">Version affichée</span>
                                    <select
                                        value={version}
                                        onChange={(e) => setVersion(e.target.value)}
                                        aria-label="Versions précédentes"
                                        style={{ ...fieldStyle(), width: "auto", fontSize: 13, fontWeight: 700, padding: "10px 14px", fontFamily, appearance: "auto" as any, color: DV2.muted }}
                                    >
                                        {versions.map((v) => (
                                            <option key={v.version} value={v.version}>
                                                {v.version === APP_VERSION ? `Version ${v.version}, en ligne` : `Version ${v.version}`}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}
                        </header>

                        {isError && !isLoading && (
                            <Card tint><div style={{ fontSize: 14, color: DV2.textSoft }}>Les nouveautés de cette version ne sont pas disponibles pour le moment.</div></Card>
                        )}

                        {/* Nouveauté principale */}
                        {featured && (
                            <section aria-label="Nouveauté principale" style={{ background: primary, color: "#fff", borderRadius: 22, padding: "28px 32px" }} className={`grid gap-7 items-center ${featured.preview ? "lg:grid-cols-[minmax(0,1fr)_300px]" : "grid-cols-1"}`}>
                                <div className="flex flex-col gap-3">
                                    <div style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".06em", opacity: 0.85 }}>La nouveauté qui change votre quotidien</div>
                                    <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.2 }}>{featured.title}</div>
                                    <div style={{ fontSize: 14, opacity: 0.9, lineHeight: 1.55 }}>{featured.description}</div>
                                    <div className="flex flex-wrap gap-2" style={{ marginTop: 4 }}>
                                        {changelogEntryPath(featured) && (
                                            <button type="button" onClick={tryFeatured} style={{ fontFamily, padding: "10px 16px", borderRadius: 12, background: "#fff", color: primary, fontWeight: 800, fontSize: 13, border: "none", cursor: "pointer" }}>Essayer maintenant</button>
                                        )}
                                        <button type="button" onClick={showFeaturedInList} style={{ fontFamily, padding: "10px 16px", borderRadius: 12, background: "transparent", color: "#fff", fontWeight: 800, fontSize: 13, border: "1px solid rgba(255,255,255,.5)", cursor: "pointer" }}>Voir comment ça marche</button>
                                    </div>
                                </div>
                                {featured.preview === "calendar" && (
                                    <div aria-hidden style={{ background: "rgba(255,255,255,.12)", borderRadius: 16, padding: 18, display: "flex", flexDirection: "column", gap: 10 }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.85 }}>Aperçu</div>
                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
                                            {CALENDAR_DAYS.map((d) => {
                                                const checked = CALENDAR_CHECKED.includes(d);
                                                return <div key={d} style={{ height: 26, borderRadius: 6, display: "grid", placeItems: "center", fontSize: 11, fontWeight: 800, background: checked ? "#fff" : "rgba(255,255,255,.15)", color: checked ? primary : "#fff" }}>{d}</div>;
                                            })}
                                        </div>
                                        {featured.previewCaption && <div style={{ fontSize: 12, opacity: 0.9 }}>{featured.previewCaption}</div>}
                                    </div>
                                )}
                            </section>
                        )}

                        {/* Nouveautés par groupe */}
                        {total > 0 && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                                {groups.map((group) => (
                                    <Card key={group.key} padding={22} as="section" aria-label={group.label} style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                                        <div className="flex items-center justify-between gap-3" style={{ marginBottom: 8 }}>
                                            <div className="flex items-center gap-2">
                                                <span style={{ color: primary, display: "flex" }}>{CATEGORY_ICONS[group.key]}</span>
                                                <h2 style={{ fontWeight: 800, fontSize: 16, margin: 0 }}>{group.label}</h2>
                                            </div>
                                            <span style={{ fontSize: 12, color: DV2.muted, fontWeight: 700 }}>{countLabel(group.entries.length)}</span>
                                        </div>
                                        {group.entries.map((entry, index) => {
                                            const key = entryKey(group.key, index);
                                            const open = openKeys.has(key);
                                            const flash = flashKey === key;
                                            return (
                                                <div key={key} ref={(el) => { entryRefs.current[key] = el; }} style={{ padding: open ? "16px 0" : "14px 0", borderBottom: index === group.entries.length - 1 ? "none" : `1px solid ${DV2.borderSoft}`, background: flash ? DV2.tint : "transparent", borderRadius: flash ? 10 : 0, transition: "background .3s" }}>
                                                    <button type="button" onClick={() => toggle(key)} aria-expanded={open} aria-controls={`${key}-panel`} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily, color: text, textAlign: "left" }}>
                                                        <span style={{ fontWeight: open ? 800 : 700, fontSize: 15 }}>{entry.title}</span>
                                                        <span aria-hidden style={{ color: DV2.placeholder, fontSize: 13, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }}>⌄</span>
                                                    </button>
                                                    {open && (
                                                        <div id={`${key}-panel`} className="flex flex-col gap-2" style={{ marginTop: 8 }}>
                                                            <div style={{ fontSize: 14, color: DV2.textSoft, lineHeight: 1.55 }}>{entry.description}</div>
                                                            {whereLink(entry)}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </Card>
                                ))}
                            </div>
                        )}

                        <Card padding={22} tint className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div style={{ fontSize: 14, fontWeight: 700 }}>Une nouveauté vous pose question, ou il vous manque quelque chose ?</div>
                            <div className="flex flex-wrap items-center gap-3">
                                <Button label="Donner mon avis" size="sm" onClick={giveFeedback} />
                                <TextLink label="Aide et contact" onClick={() => navigate(helpPath)} size={14} />
                            </div>
                        </Card>
                    </div>
                </main>
            }
        />
    );
};

export default WhatsNewPage;
